import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import useSWR from 'swr';
import { useRouter } from 'next/navigation';
import { MagnifyingGlassIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { defaultUbication, getLatLngFromAddress } from '@utils/maps';
import { useSearch, GEOLOCATION_ERRORS } from '@contexts/search';
import { mostSearchedKeywords } from './constants';
import clsx from 'clsx';
import { useFullScreenSearch } from '@contexts/fullscreensearch';
import { trackEvent } from '../../../lib/analytics/posthog';
import { EVENTS } from '../../../lib/analytics/events';
import { locationSuggestionsUrl, toLocationSuggestions } from '../../../lib/location-suggestions';

const API_URL = process.env.NEXT_PUBLIC_CYCLONE_API_URL;

const fetcher = (url) => fetch(url).then((res) => res.json());

// Si Cyclone responde con error (por ejemplo, un 503 cuando Google no encuentra la zona), no hay
// sugerencias: antes el cuerpo del error llegaba a `.map` y rompía el buscador.
const placesFetcher = (url) =>
  fetch(url)
    .then((res) => (res.ok ? res.json() : []))
    .then((data) => (Array.isArray(data) ? data : []))
    .catch(() => []);

const asArray = (value) => (Array.isArray(value) ? value : []);

const CURRENT_LOCATION_LABEL = 'Ubicación actual';
const PICKED_LOCATION_DISTANCE = 5;

const GEOLOCATION_MESSAGES = {
  [GEOLOCATION_ERRORS.denied]:
    'No tenemos permiso para usar tu ubicación. Activalo en la configuración del navegador o escribí un barrio o una ciudad.',
  [GEOLOCATION_ERRORS.timeout]:
    'Tardamos demasiado en encontrar tu ubicación. Probá de nuevo o escribí un barrio o una ciudad.',
  [GEOLOCATION_ERRORS.unavailable]: 'No pudimos obtener tu ubicación. Escribí un barrio o una ciudad.',
  [GEOLOCATION_ERRORS.unsupported]:
    'Tu navegador no permite compartir la ubicación. Escribí un barrio o una ciudad.'
};

const formatProfessionals = (count) => {
  const n = Number(count) || 0;
  return `${n.toLocaleString('es-AR')} ${n === 1 ? 'profesional' : 'profesionales'}`;
};

const SkeletonChip = () => <div className="h-9 w-28 rounded-lg bg-gray-100 animate-pulse" />;

const SkeletonRow = () => (
  <div className="flex items-center gap-3 px-2.5 py-2.5">
    <div className="w-7 h-7 rounded-lg bg-gray-100 animate-pulse flex-shrink-0" />
    <div className="h-4 w-36 rounded-sm bg-gray-100 animate-pulse" />
  </div>
);

const DropdownSkeleton = () => (
  <div>
    <div className="mb-5">
      <div className="h-3 w-40 rounded-sm bg-gray-100 animate-pulse mb-3" />
      <div className="flex flex-wrap gap-2">
        {[...Array(6)].map((_, i) => (
          <SkeletonChip key={i} />
        ))}
      </div>
    </div>
    <div>
      <div className="h-3 w-28 rounded-sm bg-gray-100 animate-pulse mb-2" />
      {[...Array(4)].map((_, i) => (
        <SkeletonRow key={i} />
      ))}
    </div>
  </div>
);

export default function SearchBar({ isNavbar = true, isFullScreen = false }) {
  const router = useRouter();
  const {
    setSearchBarParams,
    params: { query, lat, lng, distance },
    setParams,
    locationLabel,
    setLocationLabel,
    requestLocation
  } = useSearch();
  const { setShowSearchFullScreen } = useFullScreenSearch();

  const [showSearchDropdown1, setShowSearchDropdown1] = useState(false);
  const [showSearchDropdown2, setShowSearchDropdown2] = useState(false);
  const [categories, setCategories] = useState([]);
  const [keywords, setKeywords] = useState(mostSearchedKeywords);
  const [searchServices, setSearchServices] = useState([]);
  const [searchVendors, setSearchVendors] = useState([]);
  const [searchQuery, setSearchQuery] = useState(query || '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [location, setLocation] = useState(locationLabel || '');
  const [debouncedLocation, setDebouncedLocation] = useState('');
  const [locationError, setLocationError] = useState('');
  const [isLocating, setIsLocating] = useState(false);

  const refSearch1 = useRef(null);
  const refSearch2 = useRef(null);
  const dropdownRef1 = useRef(null);
  const dropdownRef2 = useRef(null);
  const containerRef = useRef(null);

  // La búsqueda de la URL y la zona elegida en otro buscador (el del inicio, el de pantalla completa)
  // se ven también acá. Se copian en el render cuando cambian, no en un efecto (un render de menos).
  const [syncedQuery, setSyncedQuery] = useState(query);
  if (query !== syncedQuery) {
    setSyncedQuery(query);
    if (query) setSearchQuery(query);
  }
  const [syncedLocationLabel, setSyncedLocationLabel] = useState(locationLabel);
  if (locationLabel !== syncedLocationLabel) {
    setSyncedLocationLabel(locationLabel);
    setLocation(locationLabel || '');
  }

  const closeDropdowns = () => {
    setShowSearchDropdown1(false);
    setShowSearchDropdown2(false);
  };

  // Ubicación con la que se busca si no se elige otra: la de la URL o la de siempre.
  const currentLocation = () =>
    lat && lng
      ? { lat, lng, distance: distance || defaultUbication.distance }
      : { lat: defaultUbication.lat, lng: defaultUbication.long, distance: defaultUbication.distance };

  // Lleva a /negocios con la búsqueda y la ubicación juntas, y cierra el desplegable o la
  // pantalla completa. Antes elegir una zona navegaba sin `query` y el overlay del teléfono
  // quedaba abierto con los dos campos vacíos.
  const goToResults = ({ query: nextQuery, lat: nextLat, lng: nextLng, distance: nextDistance }) => {
    const trimmed = (nextQuery || '').trim();
    const searchParams = new URLSearchParams();
    if (trimmed) searchParams.set('query', trimmed);
    searchParams.set('lat', String(nextLat));
    searchParams.set('lng', String(nextLng));
    searchParams.set('distance', String(nextDistance));

    setParams({ query: trimmed, lat: String(nextLat), lng: String(nextLng), distance: String(nextDistance) });
    closeDropdowns();
    if (isFullScreen) setShowSearchFullScreen(false);
    router.push(`/negocios?${searchParams.toString()}`);
  };

  const { data: locationResults, isLoading: isLocationLoading } = useSWR(
    // No se vuelve a buscar la zona que ya está elegida (sino la lista reaparece al elegirla).
    debouncedLocation.trim().length >= 2 && debouncedLocation !== locationLabel
      ? locationSuggestionsUrl(API_URL, debouncedLocation.trim())
      : null,
    placesFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  );
  const isEditingLocation = location.trim().length >= 2 && location !== locationLabel;
  const suggestions = isEditingLocation ? toLocationSuggestions(locationResults) : [];
  const noLocationMatches =
    isEditingLocation &&
    debouncedLocation === location &&
    !isLocationLoading &&
    Array.isArray(locationResults) &&
    suggestions.length === 0;

  const selectLocation = async (suggestion) => {
    trackEvent(EVENTS.SEARCH_LOCATION_SELECTED, {
      location_name: suggestion.label,
      source: 'autocomplete'
    });
    setLocation(suggestion.label);
    setLocationError('');

    const coords = suggestion.location
      ? { lat: suggestion.location.latitude, lng: suggestion.location.longitude }
      : await getLatLngFromAddress(suggestion.id);

    if (!coords?.lat || !coords?.lng) {
      setLocationError('No pudimos ubicar esa zona. Probá con otra.');
      return;
    }

    setLocationLabel(suggestion.label);
    setSearchBarParams({ lat: coords.lat, lng: coords.lng, distance: PICKED_LOCATION_DISTANCE });
    goToResults({ query: searchQuery, lat: coords.lat, lng: coords.lng, distance: PICKED_LOCATION_DISTANCE });
  };

  // «Usar mi ubicación actual»: solo se completa el campo si el navegador da la ubicación.
  const handleUseMyLocation = async () => {
    setLocationError('');
    setIsLocating(true);
    const result = await requestLocation();
    setIsLocating(false);

    if (!result.ok) {
      setLocationError(
        GEOLOCATION_MESSAGES[result.reason] || GEOLOCATION_MESSAGES[GEOLOCATION_ERRORS.unavailable]
      );
      return;
    }

    setLocation(CURRENT_LOCATION_LABEL);
    setLocationLabel(CURRENT_LOCATION_LABEL);
    setSearchBarParams({ lat: result.lat, lng: result.lng, distance: PICKED_LOCATION_DISTANCE });
    goToResults({ query: searchQuery, lat: result.lat, lng: result.lng, distance: PICKED_LOCATION_DISTANCE });
  };

  const onSearch = () => {
    // Escribió una zona y apretó «Buscar» sin elegirla de la lista: se usa la primera sugerencia.
    if (isEditingLocation && suggestions.length > 0) {
      selectLocation(suggestions[0]);
      return;
    }

    trackEvent(EVENTS.SEARCH_QUERY_SUBMITTED, {
      query: searchQuery,
      location_name: location,
      has_location: !!(location || lat),
      source: isFullScreen ? 'fullscreen' : isNavbar ? 'header' : 'hero',
      device: typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop'
    });
    goToResults({ query: searchQuery, ...currentLocation() });
  };

  const { data: _categories, isLoading: isCategoriesLoading } = useSWR(
    `${API_URL}/categories?level=2&category_parent_id=326`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onSuccess: (data) => setCategories(asArray(data))
    }
  );

  const { data: searchResults, isLoading: isSearchLoading } = useSWR(
    debouncedSearchQuery ? `${API_URL}/search/${encodeURIComponent(debouncedSearchQuery)}` : null,
    fetcher
  );

  // Vuelve a aplicar los resultados con cada tecla (no solo cuando llegan otros): si se borró la
  // búsqueda y se vuelve a escribir antes del debounce, los resultados son los mismos y hay que
  // volver a mostrarlos. Por eso queda en un efecto.
  useEffect(() => {
    if (searchResults && searchQuery !== '') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCategories(asArray(searchResults.categories));
      setKeywords(asArray(searchResults.keywords));
      setSearchServices(asArray(searchResults.services));
      setSearchVendors(asArray(searchResults.vendors));
    }
  }, [searchResults, searchQuery]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedLocation(location), 400);
    return () => clearTimeout(handler);
  }, [location]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useOutsideAlerter(refSearch1, dropdownRef1, setShowSearchDropdown1);
  useOutsideAlerter(refSearch2, dropdownRef2, setShowSearchDropdown2);

  const handleLocationChange = (e) => {
    setLocation(e.target.value);
    setLocationError('');
  };

  const handleSearchQueryChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value === '') {
      setCategories(asArray(_categories));
      setKeywords(mostSearchedKeywords);
      setSearchServices([]);
      setSearchVendors([]);
    }
  };

  // Elegir una categoría, un servicio o un término: se busca eso en la zona ya elegida.
  const searchFor = (value) => {
    setSearchQuery(value);
    setDebouncedSearchQuery(value);
    goToResults({ query: value, ...currentLocation() });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      onSearch();
    }
    if (event.key === 'Escape') {
      closeDropdowns();
    }
  };

  // Hay algo para mostrar en el desplegable del servicio. Si una búsqueda no trae nada, no se
  // abre un panel vacío con el título «Lo más buscado» encima de los resultados.
  const hasQuerySuggestions =
    categories.length > 0 || searchServices.length > 0 || keywords.length > 0 || searchVendors.length > 0;
  const isQueryDropdownLoading = (isCategoriesLoading || isSearchLoading) && categories.length === 0;

  const isDropdownOpen =
    !isFullScreen &&
    ((showSearchDropdown1 && (isQueryDropdownLoading || hasQuerySuggestions)) || showSearchDropdown2);

  // El desplegable nunca sale de la pantalla: su alto máximo es lo que queda debajo del buscador
  // (con scroll adentro). Se mide al abrirlo, antes de pintar.
  useLayoutEffect(() => {
    if (!isDropdownOpen) return;
    const panel = (showSearchDropdown1 ? dropdownRef1 : dropdownRef2).current;
    const bar = containerRef.current;
    if (!panel || !bar) return;
    const room = window.innerHeight - bar.getBoundingClientRect().bottom - 24;
    panel.style.maxHeight = `${Math.max(200, Math.min(room, window.innerHeight * 0.7))}px`;
  }, [isDropdownOpen, showSearchDropdown1]);

  // ─── Fullscreen mobile mode ────────────────────────────
  if (isFullScreen)
    return (
      <div className="flex flex-col h-full">
        {/* Search input */}
        <div className="mt-6">
          <label className="text-[11px] font-semibold tracking-[0.1em] uppercase text-gray-400 mb-2 block">
            Servicio o negocio
          </label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchQueryChange}
              onKeyDown={handleKeyDown}
              autoFocus
              placeholder="Pestañas, cejas, maquillaje..."
              className="w-full h-12 pl-11 pr-4 bg-[#F5F5F3] border border-gray-200/60 rounded-xl text-sm font-medium text-[#111] placeholder:text-gray-400 focus:outline-hidden focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>
        </div>

        {/* Location input */}
        <div className="mt-4">
          <label className="text-[11px] font-semibold tracking-[0.1em] uppercase text-gray-400 mb-2 block">
            Ubicación
          </label>
          <div className="relative">
            <MapPinIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={location}
              onChange={handleLocationChange}
              onKeyDown={handleKeyDown}
              onFocus={(e) => e.target.select()}
              placeholder="Barrio o ciudad"
              className="w-full h-12 pl-11 pr-4 bg-[#F5F5F3] border border-gray-200/60 rounded-xl text-sm font-medium text-[#111] placeholder:text-gray-400 focus:outline-hidden focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>
          {/* Location suggestions */}
          {suggestions.length > 0 && (
            <div className="mt-2 bg-white rounded-xl border border-gray-100 shadow-lg overflow-hidden">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-[#F5F5F3] transition-colors border-b border-gray-50 last:border-b-0"
                  onClick={() => selectLocation(suggestion)}
                >
                  <MapPinIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-sm text-gray-700 font-medium truncate">
                      {suggestion.label}
                    </span>
                    <span className="block text-xs text-gray-400 font-medium truncate">
                      {suggestion.secondary}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {noLocationMatches && (
            <p className="mt-2 px-1 text-xs text-gray-500 font-medium">
              No encontramos «{location.trim()}». Probá con un barrio o una ciudad.
            </p>
          )}
          {/* Use my location */}
          <button
            onClick={handleUseMyLocation}
            disabled={isLocating}
            className="flex items-center gap-2.5 mt-3 px-1 py-1.5 text-sm font-semibold text-primary-500 hover:text-primary-700 disabled:opacity-60 transition-colors"
          >
            <div className="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
              <MapPinIcon className="h-3.5 w-3.5 text-primary-500" />
            </div>
            {isLocating ? 'Buscando tu ubicación…' : 'Usar mi ubicación actual'}
          </button>
          {locationError && (
            <p
              role="alert"
              className="mt-2 px-3 py-2.5 rounded-xl bg-red-50 border border-red-100 text-xs text-red-700 font-medium leading-relaxed"
            >
              {locationError}
            </p>
          )}
        </div>

        {/* Categories + Trending */}
        {isCategoriesLoading && categories.length === 0 ? (
          <div className="mt-8">
            <div className="h-3 w-24 rounded-sm bg-gray-100 animate-pulse mb-3" />
            <div className="flex flex-wrap gap-2">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="h-10 rounded-xl bg-gray-100 animate-pulse"
                  style={{ width: `${70 + ((i * 37) % 50)}px` }}
                />
              ))}
            </div>
            <div className="mt-8">
              <div className="h-3 w-28 rounded-sm bg-gray-100 animate-pulse mb-3" />
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 animate-pulse flex-shrink-0" />
                  <div
                    className="h-4 rounded-sm bg-gray-100 animate-pulse"
                    style={{ width: `${100 + ((i * 41) % 60)}px` }}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {categories.length > 0 && (
              <div className="mt-8">
                <h3 className="text-[11px] font-semibold tracking-[0.1em] uppercase text-gray-400 mb-3">
                  Categorías
                </h3>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        trackEvent(EVENTS.SEARCH_CATEGORY_PILL_CLICKED, {
                          category_name: category.name,
                          source: 'fullscreen'
                        });
                        searchFor(category.name);
                      }}
                      className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-[#F5F5F3] border border-gray-200/60 rounded-xl hover:bg-white hover:border-primary-300 hover:text-primary-600 transition-all duration-200"
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Services with counts (when searching) or trending keywords */}
            {searchServices.length > 0 ? (
              <div className="mt-8">
                <h3 className="text-[11px] font-semibold tracking-[0.1em] uppercase text-gray-400 mb-3">
                  Servicios
                </h3>
                <div className="space-y-1">
                  {searchServices.slice(0, 5).map((service, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        trackEvent(EVENTS.SEARCH_TRENDING_KEYWORD_CLICKED, { keyword: service.name });
                        searchFor(service.name);
                      }}
                      className="flex items-center gap-3 w-full px-3 py-3 rounded-xl hover:bg-[#F5F5F3] transition-colors text-left group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-50 transition-colors">
                        <MagnifyingGlassIcon className="h-3.5 w-3.5 text-gray-400 group-hover:text-primary-500 transition-colors" />
                      </div>
                      <span className="text-sm font-medium text-gray-700 group-hover:text-[#111] transition-colors flex-1">
                        {service.name}
                      </span>
                      <span className="text-[11px] text-gray-400 font-medium tabular-nums">
                        {formatProfessionals(service.vendor_count)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : keywords.length > 0 ? (
              <div className="mt-8">
                <h3 className="text-[11px] font-semibold tracking-[0.1em] uppercase text-gray-400 mb-3">
                  Lo más buscado
                </h3>
                <div className="space-y-1">
                  {keywords.map((keyword, index) => (
                    <button
                      key={keyword}
                      onClick={() => {
                        trackEvent(EVENTS.SEARCH_TRENDING_KEYWORD_CLICKED, { keyword });
                        searchFor(keyword);
                      }}
                      className="flex items-center gap-3 w-full px-3 py-3 rounded-xl hover:bg-[#F5F5F3] transition-colors text-left group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-50 transition-colors">
                        <MagnifyingGlassIcon className="h-3.5 w-3.5 text-gray-400 group-hover:text-primary-500 transition-colors" />
                      </div>
                      <span className="text-sm font-medium text-gray-700 group-hover:text-[#111] transition-colors">
                        {keyword}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Vendor suggestions (when searching) */}
            {searchVendors.length > 0 && (
              <div className="mt-8">
                <h3 className="text-[11px] font-semibold tracking-[0.1em] uppercase text-gray-400 mb-3">
                  Profesionales
                </h3>
                <div className="space-y-1">
                  {searchVendors.slice(0, 3).map((vendor) => (
                    <button
                      key={vendor.username}
                      onClick={() => {
                        trackEvent(EVENTS.DISCOVERY_VENDOR_CARD_CLICKED, {
                          vendor_username: vendor.username,
                          source: 'search_autocomplete'
                        });
                        router.push(`/negocios/${vendor.username}`);
                        setShowSearchFullScreen(false);
                      }}
                      className="flex items-center gap-3 w-full px-3 py-3 rounded-xl hover:bg-[#F5F5F3] transition-colors text-left group"
                    >
                      {vendor.avatar_url ? (
                        <img
                          src={vendor.avatar_url}
                          alt={vendor.name || vendor.username}
                          className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-primary-500">
                            {(vendor.name || vendor.username).charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-gray-700 group-hover:text-[#111] transition-colors block truncate">
                          {vendor.name || vendor.username}
                        </span>
                        {vendor.name && (
                          <span className="text-[11px] text-gray-400 font-medium block truncate">
                            @{vendor.username}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Search button - sticky at bottom */}
        <div className="mt-auto pt-6 pb-2">
          <button
            onClick={onSearch}
            className="w-full h-14 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-medium text-sm transition-colors shadow-lg shadow-primary-500/20 active:scale-[0.98]"
          >
            Buscar
          </button>
        </div>
      </div>
    );

  // ─── Desktop / Navbar mode ─────────────────────────────

  return (
    <div
      ref={containerRef}
      className={clsx('relative', {
        'flex flex-grow w-full items-center': isNavbar,
        'w-full': !isNavbar
      })}
    >
      {/* Barra: dos campos rotulados («Qué» / «Dónde») y el botón de buscar */}
      <div className={clsx('sbar', isNavbar ? 'sbar--compact' : 'sbar--hero', { 'is-open': isDropdownOpen })}>
        <label className="sbar__field" ref={refSearch1} onClick={() => setShowSearchDropdown1(true)}>
          <span className="sbar__label">{isNavbar ? 'Qué' : 'Qué querés hacerte'}</span>
          <input
            type="text"
            className="sbar__input"
            placeholder={isNavbar ? 'Servicio o negocio' : 'Uñas, pestañas, color…'}
            onFocus={() => {
              setShowSearchDropdown1(true);
              setShowSearchDropdown2(false);
            }}
            onChange={handleSearchQueryChange}
            onKeyDown={handleKeyDown}
            value={searchQuery}
          />
        </label>

        <span className="sbar__divider" aria-hidden="true" />

        <label className="sbar__field sbar__field--where" ref={refSearch2} onClick={() => setShowSearchDropdown2(true)}>
          <span className="sbar__label">Dónde</span>
          <input
            type="text"
            className="sbar__input"
            placeholder="Barrio o ciudad"
            onFocus={(e) => {
              e.target.select();
              setShowSearchDropdown2(true);
              setShowSearchDropdown1(false);
            }}
            onChange={handleLocationChange}
            onKeyDown={handleKeyDown}
            value={location}
          />
        </label>

        <button type="button" className="sbar__go" onClick={onSearch} aria-label="Buscar">
          {isNavbar ? <MagnifyingGlassIcon className="h-4 w-4" strokeWidth={2.2} /> : 'Buscar'}
        </button>
      </div>

      {/* ─── Dropdown ───────────────────────────────────── */}
      {isDropdownOpen && (
        <div
          ref={showSearchDropdown1 ? dropdownRef1 : dropdownRef2}
          className={clsx(
            'absolute left-0 w-full top-full mt-2 dropdown-glass rounded-2xl z-50 max-h-[70vh] overflow-y-auto',
            {
              'p-4': isNavbar,
              'p-5 md:p-6': !isNavbar
            }
          )}
        >
          {/* Service dropdown */}
          {showSearchDropdown1 && (
            <div>
              {isQueryDropdownLoading ? (
                <DropdownSkeleton />
              ) : (
                <>
                  {/* Categories */}
                  {categories.length > 0 && (
                    <div className="mb-5">
                      <h3 className="text-[11px] font-semibold tracking-[0.1em] uppercase text-gray-400 mb-3">
                        Explorar por categoría
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {categories?.map((category, index) => (
                          <button
                            key={index}
                            className="px-3.5 py-2 text-sm font-medium text-gray-700 bg-[#F5F5F3] rounded-lg hover:bg-primary-50 hover:text-primary-600 border border-transparent hover:border-primary-200 transition-all duration-200 cursor-pointer"
                            onClick={() => {
                              trackEvent(EVENTS.SEARCH_CATEGORY_PILL_CLICKED, {
                                category_name: category.name,
                                source: 'search_dropdown'
                              });
                              searchFor(category.name);
                            }}
                          >
                            {category.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Services with counts (when searching) or trending keywords */}
                  {searchServices.length > 0 ? (
                    <div>
                      <h3 className="text-[11px] font-semibold tracking-[0.1em] uppercase text-gray-400 mb-2">
                        Servicios
                      </h3>
                      <div className="space-y-0.5">
                        {searchServices.slice(0, 5).map((service, index) => (
                          <button
                            key={index}
                            className="flex items-center gap-3 w-full px-2.5 py-2.5 rounded-xl hover:bg-[#F5F5F3] transition-colors cursor-pointer text-left group"
                            onClick={() => {
                              trackEvent(EVENTS.SEARCH_TRENDING_KEYWORD_CLICKED, { keyword: service.name });
                              searchFor(service.name);
                            }}
                          >
                            <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-50 transition-colors">
                              <MagnifyingGlassIcon className="h-3.5 w-3.5 text-gray-400 group-hover:text-primary-500 transition-colors" />
                            </div>
                            <span className="text-sm font-medium text-gray-600 group-hover:text-[#111] transition-colors flex-1">
                              {service.name}
                            </span>
                            <span className="text-[11px] text-gray-400 font-medium tabular-nums">
                              {formatProfessionals(service.vendor_count)}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : keywords.length > 0 ? (
                    <div>
                      <h3 className="text-[11px] font-semibold tracking-[0.1em] uppercase text-gray-400 mb-2">
                        Lo más buscado
                      </h3>
                      <div className="space-y-0.5">
                        {keywords.slice(0, 5).map((keyword, index) => (
                          <button
                            key={index}
                            className="flex items-center gap-3 w-full px-2.5 py-2.5 rounded-xl hover:bg-[#F5F5F3] transition-colors cursor-pointer text-left group"
                            onClick={() => {
                              trackEvent(EVENTS.SEARCH_TRENDING_KEYWORD_CLICKED, { keyword });
                              searchFor(keyword);
                            }}
                          >
                            <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-50 transition-colors">
                              <MagnifyingGlassIcon className="h-3.5 w-3.5 text-gray-400 group-hover:text-primary-500 transition-colors" />
                            </div>
                            <span className="text-sm font-medium text-gray-600 group-hover:text-[#111] transition-colors">
                              {keyword}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* Vendor suggestions (when searching) */}
                  {searchVendors.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <h3 className="text-[11px] font-semibold tracking-[0.1em] uppercase text-gray-400 mb-2">
                        Profesionales
                      </h3>
                      <div className="space-y-0.5">
                        {searchVendors.slice(0, 3).map((vendor) => (
                          <button
                            key={vendor.username}
                            className="flex items-center gap-3 w-full px-2.5 py-2.5 rounded-xl hover:bg-[#F5F5F3] transition-colors cursor-pointer text-left group"
                            onClick={() => {
                              trackEvent(EVENTS.DISCOVERY_VENDOR_CARD_CLICKED, {
                                vendor_username: vendor.username,
                                source: 'search_autocomplete'
                              });
                              router.push(`/negocios/${vendor.username}`);
                              setShowSearchDropdown1(false);
                            }}
                          >
                            {vendor.avatar_url ? (
                              <img
                                src={vendor.avatar_url}
                                alt={vendor.name || vendor.username}
                                className="w-7 h-7 rounded-lg object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                                <span className="text-[11px] font-bold text-primary-500">
                                  {(vendor.name || vendor.username).charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div className="min-w-0">
                              <span className="text-sm font-medium text-gray-600 group-hover:text-[#111] transition-colors block truncate">
                                {vendor.name || vendor.username}
                              </span>
                              {vendor.name && (
                                <span className="text-[11px] text-gray-400 font-medium block truncate">
                                  @{vendor.username}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Location dropdown */}
          {showSearchDropdown2 && (
            <div>
              {/* Use my location */}
              <button
                className="flex items-center gap-3 w-full px-2.5 py-3 rounded-xl hover:bg-[#F5F5F3] transition-colors cursor-pointer text-left group disabled:opacity-60"
                onClick={handleUseMyLocation}
                disabled={isLocating}
              >
                <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <MapPinIcon className="h-4 w-4 text-primary-500" />
                </div>
                <div>
                  <span className="text-sm font-semibold text-[#111] block">
                    {isLocating ? 'Buscando tu ubicación…' : 'Usar mi ubicación actual'}
                  </span>
                  <span className="text-xs text-gray-400 font-medium">Te mostramos lo que está cerca</span>
                </div>
              </button>

              {locationError && (
                <p
                  role="alert"
                  className="mt-1 mx-2.5 px-3 py-2.5 rounded-xl bg-red-50 border border-red-100 text-xs text-red-700 font-medium leading-relaxed"
                >
                  {locationError}
                </p>
              )}

              {/* Location suggestions */}
              {suggestions.length > 0 && (
                <div className="mt-1 pt-2 border-t border-gray-100">
                  <h3 className="text-[11px] font-semibold tracking-[0.1em] uppercase text-gray-400 mb-2 px-2.5">
                    Sugerencias
                  </h3>
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      className="flex items-center gap-3 w-full px-2.5 py-2.5 rounded-xl hover:bg-[#F5F5F3] transition-colors cursor-pointer text-left group"
                      onClick={() => selectLocation(suggestion)}
                    >
                      <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200/80 transition-colors">
                        <MapPinIcon className="h-3.5 w-3.5 text-gray-400" />
                      </div>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-600 group-hover:text-[#111] transition-colors truncate">
                          {suggestion.label}
                        </span>
                        <span className="block text-xs text-gray-400 font-medium truncate">
                          {suggestion.secondary}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {noLocationMatches && (
                <p className="mt-1 pt-3 px-2.5 border-t border-gray-100 text-xs text-gray-500 font-medium">
                  No encontramos «{location.trim()}». Probá con un barrio o una ciudad.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function useOutsideAlerter(inputRef, dropdownRef, setShowDropdown) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputRef, dropdownRef, setShowDropdown]);
}
